import React, { useContext } from "react";
import { useProspectProfile } from "../../../contexts/ProsepctProfileContext";
import { Accordion, AccordionBody, AccordionHeader, SubSection, useBreakpoints } from "best-common-react";
import { Body, Card, Item, Label, Row, Value } from "./ReadOnlyProfileStyles";
import { useMetadata } from "../../../contexts/MetadataContext";
import { useTranslation } from "react-i18next";
import styled, { ThemeContext } from "styled-components";
import { HeightUnits, MEXICO, WeightUnits } from "../../../constants/ProfileConstants";
import { useDropdowns } from "../../../contexts/DropdownsContext";
import { CountryIds } from "../../../constants/CountryConstants";
import { ProspectProfessionalTeamDTO } from "../../../types/ProspectProfile";
import useLanguage from "../../../hooks/useLanguage";

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

type ProfessionalTeamReadOnlyCardProps = {
  professionalTeam: ProspectProfessionalTeamDTO;
};

const ProfessionalTeamReadOnlyCard: React.FC<ProfessionalTeamReadOnlyCardProps> = ({ professionalTeam }) => {
  const { t } = useTranslation("profile");
  const { intlProfessionalLeagues, intlProfessionalTeams } = useMetadata();
  const { isEs } = useLanguage();
  const league = intlProfessionalLeagues.find((league) => league.id === professionalTeam.leagueId);
  const leagueName = isEs ? league?.esDescription : league?.description;
  const teamName = intlProfessionalTeams.find((team) => team.id === professionalTeam.leagueId)?.description;

  return (
    <Card>
      <Column style={{ width: "50%" }}>
        <div>
          <Label>{t("profile:year")}</Label>
          <Value>{professionalTeam.year}</Value>
        </div>
        <div>
          <Label>{t("profile:clubLocation")}</Label>
          <Value>{professionalTeam.clubLocation}</Value>
        </div>
      </Column>
      <Column style={{ width: "50%" }}>
        <div>
          <Label>{t("profile:leagueName")}</Label>
          <Value>{leagueName}</Value>
        </div>
        <div>
          <Label>{t("profile:clubName")}</Label>
          <Value>{teamName}</Value>
        </div>
      </Column>
    </Card>
  );
};

const BaseballInfoReadyOnly: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { profile } = useProspectProfile();
  const { isMobile } = useBreakpoints();
  const Theme = useContext(ThemeContext);
  const { positions, battingSides, throwingHands } = useMetadata();
  const { getMexicanLeagueTeamOptions, getYesNoRadioValues } = useDropdowns();

  const position = positions.find((p) => p.positionId === profile?.positionId);
  const bats = battingSides.find((p) => p.battingSideId === profile?.batsId);
  const throws = throwingHands.find((p) => p.throwingHandId === profile?.throwingHandId);
  const height =
    profile?.heightUnitId === HeightUnits.CM
      ? profile.height
      : `${Math.floor(profile.height / 12)}'${profile.height % 12}"`;
  const weight = profile?.weightUnitId === WeightUnits.KG ? `${profile.weight} kg` : `${profile.weight} lbs`;

  return (
    <Accordion
      accordionKey="basball-info"
      styles={{ ...Theme.accordion, body: { ...Theme.accordion.body, background: Theme.colors.cloud, border: "none" } }}
    >
      <AccordionHeader>{t("profile:baseballInformationSection")}</AccordionHeader>
      <AccordionBody>
        <Body>
          <Row>
            <Item isMobile={isMobile}>
              <Label>{t("profile:position")}</Label>
              <Value>{i18n.language === "en" ? position?.description : position?.esDescription}</Value>
            </Item>
          </Row>
          <Row>
            <Item isMobile={isMobile}>
              <Label>{t("profile:bats")}</Label>
              <Value>{i18n.language === "en" ? bats?.description : bats?.esDescription}</Value>
            </Item>
            <Item isMobile={isMobile}>
              <Label>{t("profile:throws")}</Label>
              <Value>{i18n.language === "en" ? throws?.description : throws?.esDescription}</Value>
            </Item>
          </Row>
          <Row>
            <Item isMobile={isMobile}>
              <Label>
                {profile?.heightUnitId === HeightUnits.CM ? t("profile:heightCm") : t("profile:heightFtIn")}
              </Label>
              <Value>{height}</Value>
            </Item>
            <Item isMobile={isMobile}>
              <Label>{t("profile:weight")}</Label>
              <Value>{weight}</Value>
            </Item>
          </Row>
          {profile?.registrationCountryId === MEXICO.id && (
            <Row>
              <Item isMobile={isMobile}>
                <Label>{t("profile:mexicanLeagueTeam")}</Label>
                <Value>{getMexicanLeagueTeamOptions(profile.mexicanLeagueTeamId)?.label || "N/A"}</Value>
              </Item>
            </Row>
          )}
          {profile?.birthCountryId === CountryIds.CUBA && (
            <>
              <Row>
                <div>
                  <Label>{t("profile:participatedInProBaseball")}</Label>
                  <Value>{getYesNoRadioValues(profile?.participatedInProBaseball)?.label}</Value>
                </div>
              </Row>
              {profile?.participatedInProBaseball === true && (
                <SubSection header={t("profile:teams")}>
                  {profile?.professionalTeams.map((proTeam) => (
                    <ProfessionalTeamReadOnlyCard
                      key={`professional-team-card-${proTeam.teamId}`}
                      professionalTeam={proTeam}
                    />
                  ))}
                </SubSection>
              )}
            </>
          )}
        </Body>
      </AccordionBody>
    </Accordion>
  );
};

export default BaseballInfoReadyOnly;
